import PptxGenJS from "pptxgenjs";

export async function exportDonorReportPpt({
  companyName = "",
  dateFrom,
  dateTo,
  darkMode,
  summary,
  materialBreakdown,
  receiverBreakdown,
  trend,
  isPerReceiver = false,
  receiverName = "",
  sections = {},
} = {}) {
  // section defaults (all on if not specified)
  const showTrendChart = sections.trendChart !== false;
  const showTrendTable = sections.trendTable !== false;
  const showMatChart = sections.materialChart !== false;
  const showMatTable = sections.materialTable !== false;
  const showRxTable = sections.receiverTable !== false;
  const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // format "2026-01-01" → "January 1, 2026"
  const fmtDate = (d) => {
    if (!d) return "All time";
    const parts = d.split("-");
    if (parts.length !== 3) return d;
    const [y, m, day] = parts;
    const month = MONTH_NAMES[parseInt(m, 10) - 1] || m;
    return `${month} ${parseInt(day, 10)}, ${y}`;
  };

  // format "2026-01" → "January 2026"
  const fmtPeriod = (p) => {
    if (!p) return p;
    const parts = p.split("-");
    if (parts.length !== 2) return p;
    const [y, m] = parts;
    const month = MONTH_NAMES[parseInt(m, 10) - 1] || m;
    return `${month} ${y}`;
  };

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const isAllTime = !dateFrom && !dateTo;
  const effectiveFrom = dateFrom || summary?.earliestDate || null;
  const effectiveTo = dateTo || todayStr;

  // cover slide
  const dateRangeDisplay = isAllTime
    ? `All time  (${fmtDate(effectiveFrom)} → ${fmtDate(effectiveTo)})`
    : `${fmtDate(dateFrom)}  →  ${fmtDate(dateTo)}`;

  // footer: keep it short
  const footerDateRange = isAllTime
    ? "All time"
    : `${fmtDate(dateFrom)} → ${fmtDate(dateTo)}`;

  const generatedDate = fmtDate(todayStr);

  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";

  // same theme as receiver report for consistency
  const THEME = darkMode
    ? {
        bg: "070B14",
        panel: "0B1220",
        text: "FFFFFF",
        muted: "B9C6D6",
        accent: "67A3FF",
        accent2: "2F6FED",
        border: "1D2A44",
        soft: "0F1A2E",
      }
    : {
        bg: "FFFFFF",
        panel: "F6F9FF",
        text: "0A1222",
        muted: "40506A",
        accent: "2F6FED",
        accent2: "67A3FF",
        border: "D7E2F2",
        soft: "EEF4FF",
      };

  const SLIDE_W = 13.33;

  // dynamic table height based on row count (header + body rows)
  const ROW_H = 0.35;
  const MAX_TABLE_H = 4.85;
  const tableH = (rowCount) => Math.min((rowCount + 1) * ROW_H, MAX_TABLE_H);
  const bgH = (rowCount) => tableH(rowCount) + 0.75;

  const fmtNum = (n, digits = 0) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "—";
    return digits > 0 ? x.toFixed(digits) : x.toLocaleString();
  };

  // shared slide helpers
  const addBackground = (slide) => {
    slide.background = { color: THEME.bg };
    slide.addShape(pptx.ShapeType.rect, {
      x: 0, y: 0, w: SLIDE_W, h: 0.12,
      fill: { color: THEME.accent2 },
    });
    slide.addShape(pptx.ShapeType.rect, {
      x: 0, y: 0.12, w: SLIDE_W, h: 0.28,
      fill: { color: THEME.soft },
    });
  };

  const addHeader = (slide, title, subtitle) => {
    slide.addText(title, {
      x: 0.6, y: 0.45, w: 12.2, h: 0.45,
      fontFace: "Aptos Display", fontSize: 26, bold: true, color: THEME.text,
    });
    if (subtitle) {
      slide.addText(subtitle, {
        x: 0.6, y: 0.92, w: 12.2, h: 0.3,
        fontFace: "Aptos", fontSize: 12, color: THEME.muted,
      });
    }
  };

  const addFooter = (slide, pageNum, pageTotal) => {
    slide.addText(`Donation Impact Report  •  ${footerDateRange}`, {
      x: 0.6, y: 7.12, w: 9.8, h: 0.25,
      fontFace: "Aptos", fontSize: 10, color: THEME.muted,
    });
    slide.addText(`Page ${pageNum}/${pageTotal}`, {
      x: 11.2, y: 7.12, w: 1.5, h: 0.25,
      align: "right", fontFace: "Aptos", fontSize: 10, color: THEME.muted,
    });
  };

  const addCard = (slide, { x, y, w, h, title, value, sub }) => {
    slide.addShape(pptx.ShapeType.rect, {
      x, y, w, h,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
    });
    // left accent bar
    slide.addShape(pptx.ShapeType.rect, {
      x, y, w: 0.12, h,
      fill: { color: THEME.accent2 },
    });
    slide.addText(title, {
      x: x + 0.25, y: y + 0.18, w: w - 0.35, h: 0.25,
      fontFace: "Aptos", fontSize: 11, color: THEME.muted,
    });
    slide.addText(value, {
      x: x + 0.25, y: y + 0.48, w: w - 0.35, h: 0.5,
      fontFace: "Aptos Display", fontSize: 20, bold: true, color: THEME.text,
    });
    if (sub) {
      slide.addText(sub, {
        x: x + 0.25, y: y + 0.98, w: w - 0.35, h: 0.25,
        fontFace: "Aptos", fontSize: 10, color: THEME.muted,
      });
    }
  };

  // table pagination
  const MAX_ROWS = 14;
  const matRows = materialBreakdown || [];
  const rxRows = receiverBreakdown || [];
  const trendData = trend || [];

  const chunk = (arr) =>
    arr.length
      ? Array.from({ length: Math.ceil(arr.length / MAX_ROWS) }, (_, i) =>
          arr.slice(i * MAX_ROWS, (i + 1) * MAX_ROWS))
      : [[]];

  const matChunks = chunk(matRows);
  const rxChunks = !isPerReceiver && rxRows.length && showRxTable ? chunk(rxRows) : [];
  const hasTrend = trendData.length > 0;
  const trendChunkCount = hasTrend && showTrendTable ? Math.ceil(trendData.length / MAX_ROWS) : 0;

  // chart slides: 1 for GHG trend chart (if trend data), 1 for material donut (if materials)
  const hasTrendChart = hasTrend && trendData.length >= 2 && showTrendChart;
  const hasMatChart = matRows.length >= 2 && showMatChart;

  const totalSlides = 1
    + (hasTrendChart ? 1 : 0)
    + trendChunkCount
    + (hasMatChart ? 1 : 0)
    + (showMatTable ? matChunks.length : 0)
    + rxChunks.length;
  let page = 1;

  // slide 1: cover + KPIs
  {
    const slide = pptx.addSlide();
    addBackground(slide);

    const title = isPerReceiver
      ? `Donation Impact Report — ${receiverName}`
      : "Donation Impact Report";
    addHeader(slide, title, companyName || undefined);

    // date range panel
    slide.addShape(pptx.ShapeType.rect, {
      x: 0.6, y: 1.4, w: 12.2, h: 1.6,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
    });
    slide.addText("Date range", {
      x: 1.0, y: 1.55, w: 5.5, h: 0.3,
      fontFace: "Aptos", fontSize: 12, color: THEME.muted,
    });
    slide.addText(dateRangeDisplay, {
      x: 1.0, y: 1.85, w: 11.0, h: 0.5,
      fontFace: "Aptos Display", fontSize: 22, bold: true, color: THEME.text,
    });
    slide.addText(`Generated on ${generatedDate}`, {
      x: 1.0, y: 2.4, w: 11.0, h: 0.3,
      fontFace: "Aptos", fontSize: 10, color: THEME.muted,
    });

    // 4 KPI cards
    const kY = 3.3;
    const kH = 1.55;
    const gap = 0.15;
    const kX0 = 0.6;
    const kW = (12.2 - gap * 3) / 4;

    addCard(slide, {
      x: kX0, y: kY, w: kW, h: kH,
      title: "Total donated",
      value: `${fmtNum(summary?.totalQty)} lbs`,
      sub: "Across all materials",
    });
    addCard(slide, {
      x: kX0 + (kW + gap), y: kY, w: kW, h: kH,
      title: "Receivers served",
      value: String(summary?.totalReceivers || 0),
      sub: "Unique recycling partners",
    });
    addCard(slide, {
      x: kX0 + (kW + gap) * 2, y: kY, w: kW, h: kH,
      title: "GHG impact enabled",
      value: `${fmtNum(summary?.totalAvoided)} kg`,
      sub: "CO₂e avoided through donations",
    });
    addCard(slide, {
      x: kX0 + (kW + gap) * 3, y: kY, w: kW, h: kH,
      title: "Total deliveries",
      value: String(summary?.totalDeliveries || 0),
      sub: "Completed in date range",
    });

    addFooter(slide, page++, totalSlides);
  }

  // chart color palette
  const CHART_COLORS = [
    "2F6FED", "34C759", "FF9500", "AF52DE", "FF3B30",
    "5AC8FA", "FFCC00", "FF2D55", "64D2FF", "30D158",
  ];

  // GHG trend bar chart slide
  if (hasTrendChart) {
    const slide = pptx.addSlide();
    addBackground(slide);
    addHeader(slide, "GHG Avoided Over Time", "Monthly donation quantities and emissions avoided");

    slide.addShape(pptx.ShapeType.rect, {
      x: 0.6, y: 1.35, w: 12.2, h: 5.6,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
    });

    const chartLabels = trendData.map((r) => fmtPeriod(r.period));
    const chartData = [
      {
        name: "Qty Donated (lbs)",
        labels: chartLabels,
        values: trendData.map((r) => Number(r.qty) || 0),
      },
      {
        name: "GHG Avoided (kg CO₂e)",
        labels: chartLabels,
        values: trendData.map((r) => Number(r.avoided) || 0),
      },
    ];

    slide.addChart(pptx.charts.BAR, chartData, {
      x: 0.9, y: 1.55, w: 11.6, h: 5.1,
      barDir: "col",
      barGapWidthPct: 75,
      chartColors: [THEME.accent2, "34C759"],
      showLegend: true,
      legendPos: "b",
      legendColor: THEME.muted,
      legendFontSize: 9,
      catAxisLabelColor: THEME.muted,
      catAxisLabelFontSize: 9,
      valAxisLabelColor: THEME.muted,
      valAxisLabelFontSize: 9,
      catAxisOrientation: "minMax",
      valAxisOrientation: "minMax",
      showValue: false,
    });

    addFooter(slide, page++, totalSlides);
  }

  // GHG trend table slides
  if (hasTrend && showTrendTable) {
    const trendChunks = chunk(trendData);

    trendChunks.forEach((rows, idx) => {
      const slide = pptx.addSlide();
      addBackground(slide);

      const suffix = trendChunks.length > 1 ? ` (${idx + 1}/${trendChunks.length})` : "";
      addHeader(
        slide,
        `GHG Avoided Over Time${suffix}`,
        "Emissions avoided through your material donations (monthly)",
      );

      slide.addShape(pptx.ShapeType.rect, {
        x: 0.6, y: 1.35, w: 12.2, h: bgH(rows.length),
        fill: { color: THEME.panel },
        line: { color: THEME.border, width: 1 },
      });

      const headerRow = [
        { text: "Period", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
        { text: "Qty Donated (lbs)", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
        { text: "GHG Avoided (kg CO₂e)", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      ];

      const bodyRows = rows.map((r, i) => {
        const fill = i % 2 === 1 ? THEME.soft : THEME.panel;
        return [
          { text: fmtPeriod(r.period), options: { color: THEME.text, fill } },
          { text: fmtNum(r.qty, 1), options: { color: THEME.text, fill } },
          { text: fmtNum(r.avoided, 1), options: { color: THEME.text, fill } },
        ];
      });

      slide.addTable([headerRow, ...bodyRows], {
        x: 0.9, y: 1.75, w: 11.6, h: tableH(rows.length),
        fontFace: "Aptos", fontSize: 10, color: THEME.text,
        border: { type: "solid", color: THEME.border, pt: 1 },
        colW: [3.8, 3.9, 3.9],
      });

      addFooter(slide, page++, totalSlides);
    });
  }

  // material breakdown donut chart slide
  if (hasMatChart) {
    const slide = pptx.addSlide();
    addBackground(slide);
    addHeader(slide, "Material Distribution", "Breakdown of donated materials by quantity");

    slide.addShape(pptx.ShapeType.rect, {
      x: 0.6, y: 1.35, w: 12.2, h: 5.6,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
    });

    // top 8 materials + group rest as "Other"
    const MAX_SLICES = 8;
    const sorted = [...matRows].sort((a, b) => (Number(b.qty) || 0) - (Number(a.qty) || 0));
    let donutLabels, donutValues;

    if (sorted.length <= MAX_SLICES) {
      donutLabels = sorted.map((r) => r.name || "—");
      donutValues = sorted.map((r) => Number(r.qty) || 0);
    } else {
      const top = sorted.slice(0, MAX_SLICES);
      const otherQty = sorted.slice(MAX_SLICES).reduce((s, r) => s + (Number(r.qty) || 0), 0);
      donutLabels = [...top.map((r) => r.name || "—"), "Other"];
      donutValues = [...top.map((r) => Number(r.qty) || 0), otherQty];
    }

    const donutData = [{
      name: "Materials",
      labels: donutLabels,
      values: donutValues,
    }];

    slide.addChart(pptx.charts.DOUGHNUT, donutData, {
      x: 1.5, y: 1.55, w: 10.3, h: 5.1,
      holeSize: 55,
      chartColors: CHART_COLORS.slice(0, donutLabels.length),
      showLegend: true,
      legendPos: "r",
      legendColor: THEME.text,
      legendFontSize: 10,
      dataLabelColor: THEME.text,
      dataLabelFontSize: 9,
      showPercent: true,
      showValue: false,
      showTitle: false,
    });

    addFooter(slide, page++, totalSlides);
  }

  // material breakdown table slides
  if (showMatTable) {
    const headerRow = [
      { text: "Material", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "Qty (lbs)", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "Avoided (kg CO₂e)", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "% of Total", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
    ];

    const emptyRow = Array(4).fill(null).map(() => ({
      text: "—", options: { color: THEME.muted, fill: THEME.panel },
    }));

    matChunks.forEach((rows, idx) => {
      const slide = pptx.addSlide();
      addBackground(slide);

      const suffix = matChunks.length > 1 ? ` (${idx + 1}/${matChunks.length})` : "";
      addHeader(slide, `Donations by Material${suffix}`, "Material breakdown sorted by quantity");

      const bodyRows = rows.length > 0
        ? rows.map((r, i) => {
            const fill = i % 2 === 1 ? THEME.soft : THEME.panel;
            return [
              { text: r.name, options: { color: THEME.text, fill } },
              { text: fmtNum(r.qty, 1), options: { color: THEME.text, fill } },
              { text: fmtNum(r.avoided, 1), options: { color: THEME.text, fill } },
              { text: `${r.percentage}%`, options: { color: THEME.text, fill } },
            ];
          })
        : [emptyRow];

      slide.addShape(pptx.ShapeType.rect, {
        x: 0.6, y: 1.35, w: 12.2, h: bgH(bodyRows.length),
        fill: { color: THEME.panel },
        line: { color: THEME.border, width: 1 },
      });

      slide.addTable([headerRow, ...bodyRows], {
        x: 0.9, y: 1.75, w: 11.6, h: tableH(bodyRows.length),
        fontFace: "Aptos", fontSize: 10, color: THEME.text,
        border: { type: "solid", color: THEME.border, pt: 1 },
        colW: [4.0, 2.5, 3.0, 2.1],
      });

      addFooter(slide, page++, totalSlides);
    });
  }

  // receiver breakdown table slides (skipped for per-receiver reports)
  if (rxChunks.length > 0) {
    const headerRow = [
      { text: "Receiver", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "Location", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "Qty (lbs)", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "Avoided (kg CO₂e)", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "% of Total", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
    ];

    rxChunks.forEach((rows, idx) => {
      const slide = pptx.addSlide();
      addBackground(slide);

      const suffix = rxChunks.length > 1 ? ` (${idx + 1}/${rxChunks.length})` : "";
      addHeader(slide, `Donations by Receiver${suffix}`, "Receiver breakdown sorted by quantity");

      const bodyRows = rows.map((r, i) => {
        const fill = i % 2 === 1 ? THEME.soft : THEME.panel;
        return [
          { text: r.name, options: { color: THEME.text, fill } },
          { text: r.location || "—", options: { color: THEME.text, fill } },
          { text: fmtNum(r.totalQty, 1), options: { color: THEME.text, fill } },
          { text: fmtNum(r.avoided, 1), options: { color: THEME.text, fill } },
          { text: `${r.avoidedPercentage}%`, options: { color: THEME.text, fill } },
        ];
      });

      slide.addShape(pptx.ShapeType.rect, {
        x: 0.6, y: 1.35, w: 12.2, h: bgH(bodyRows.length),
        fill: { color: THEME.panel },
        line: { color: THEME.border, width: 1 },
      });

      slide.addTable([headerRow, ...bodyRows], {
        x: 0.9, y: 1.75, w: 11.6, h: tableH(bodyRows.length),
        fontFace: "Aptos", fontSize: 10, color: THEME.text,
        border: { type: "solid", color: THEME.border, pt: 1 },
        colW: [3.2, 2.0, 2.2, 2.4, 1.8],
      });

      addFooter(slide, page++, totalSlides);
    });
  }

  const safeFrom = dateFrom || "all";
  const safeTo = dateTo || "all";
  await pptx.writeFile({ fileName: `Donation_Report_${safeFrom}_to_${safeTo}.pptx` });
}
